import React, { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { Mic } from "lucide-react";
import "../../assets/css/AskAI.css";
import { getProjectPid } from "../../utils/sessionProject";
import { getPromptByType } from "../../services/promptService";
import LangSelect from "./LangSelect";

const FALLBACK_GREETING = "Welcome! 🚀 I’m here to answer any questions you may have. How can I assist you today?";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_BASE_URL;

// Minimum pre-roll before the first audio chunk starts — kept small to cut
// perceived latency between the response arriving and speech starting.
const PLAYBACK_PREROLL_SEC = 0.12;
const SPEAKING_RMS_THRESHOLD = 0.02;
// Threshold for cutting off AI audio/typing the instant the user starts
// talking over it. Was 0.06 (required raised-voice volume to ever fire,
// leaving normal-volume speech to fall through to the much slower
// STT-round-trip path). Lowered close to SPEAKING_RMS_THRESHOLD so ordinary
// speech triggers it within the first word or two.
const BARGE_IN_RMS_THRESHOLD = 0.03;

// Fallback pace only used if a clause ever arrives without durationMs.
const FALLBACK_MS_PER_WORD = 220;

// ── PCM 24kHz Playback ──────────────────────────────────────────────────────
let playContext = null;
let nextPlayTime = 0;
// onStart timeouts scheduled below are plain JS timers, not tied to the
// AudioContext — closing the context in stopLocalPlayback() does NOT cancel
// them. Without tracking + clearing them here, a clause's reveal can still
// fire after an interrupt, starting a fresh typing bubble a beat later.
let pendingStartTimeouts = [];

// `onStart` fires right when this chunk's audio actually begins playing —
// used to reveal the matching clause's caption text in sync with speech.
function playPCMAudio(arrayBuffer, onStart) {
  if (!playContext || playContext.state === "closed") {
    playContext = new AudioContext({ sampleRate: 24000 });
    nextPlayTime = 0;
  }
  const ctx = playContext;
  const schedule = () => {
    let bytes = arrayBuffer instanceof ArrayBuffer ? arrayBuffer : arrayBuffer.buffer;
    let byteLen = bytes.byteLength % 2 === 0 ? bytes.byteLength : bytes.byteLength - 1;
    if (byteLen < 2) return;
    const int16 = new Int16Array(bytes, 0, byteLen / 2);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;
    const buf = ctx.createBuffer(1, float32.length, 24000);
    buf.getChannelData(0).set(float32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const startAt = nextPlayTime === 0
      ? ctx.currentTime + PLAYBACK_PREROLL_SEC
      : Math.max(ctx.currentTime + 0.01, nextPlayTime);
    src.start(startAt);
    nextPlayTime = startAt + buf.duration;
    if (onStart) {
      const delayMs = Math.max(0, (startAt - ctx.currentTime) * 1000);
      const id = setTimeout(onStart, delayMs);
      pendingStartTimeouts.push(id);
    }
  };
  ctx.state === "suspended" ? ctx.resume().then(schedule) : schedule();
}

function stopLocalPlayback() {
  if (playContext && playContext.state !== "closed") playContext.close();
  playContext = null;
  nextPlayTime = 0;
  pendingStartTimeouts.forEach(id => clearTimeout(id));
  pendingStartTimeouts = [];
}

// Ground truth for "is AI audio actually still coming out of the speakers" —
// deliberately independent of aiSpeakingRef, which is driven by backend
// status/tts_clause events and a fixed 600ms grace timer. Those events (esp.
// "idle") can fire well before the already-scheduled audio buffers finish
// playing, silently disarming barge-in detection for seconds while the AI
// is still audibly talking. This checks the real Web Audio clock instead.
function isAudioActuallyPlaying() {
  return !!playContext && playContext.state !== "closed" && nextPlayTime > playContext.currentTime;
}

// Real remaining playback time for whatever's currently scheduled — used to
// tell the backend when a turn's audio has genuinely finished playing (it
// only knows when generation/emission finished, which is earlier).
function getScheduledAudioEndDelayMs() {
  if (!playContext || playContext.state === "closed") return 0;
  return Math.max(0, (nextPlayTime - playContext.currentTime) * 1000);
}

// ── Mic waveform visualizer (WhatsApp voice-note style) ──────────────────────
// Bars enter from the right as new amplitude samples come in and scroll left,
// instead of a live reactive spectrum — reads as a "recording" waveform.
const WAVE_BAR_COUNT = 44;
const WAVE_SAMPLE_INTERVAL_MS = 70;
const WAVE_MIN_LEVEL = 0.08;

function readAmplitude(analyser, timeDomainBuffer) {
  analyser.getByteTimeDomainData(timeDomainBuffer);
  let maxDeviation = 0;
  for (let i = 0; i < timeDomainBuffer.length; i++) {
    const deviation = Math.abs(timeDomainBuffer[i] - 128) / 128;
    if (deviation > maxDeviation) maxDeviation = deviation;
  }
  return maxDeviation;
}

function drawWaveform(canvas, history, speaking) {
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth || canvas.width;
  const height = canvas.clientHeight || canvas.height;
  if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
    canvas.width = width * dpr;
    canvas.height = height * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const count = history.length;
  const gap = 3;
  const barWidth = Math.max(2, (width - gap * (count - 1)) / count);
  const centerY = height / 2;

  ctx.fillStyle = speaking ? "#4CAF33" : "#b7c9e6";

  for (let i = 0; i < count; i++) {
    const barHeight = Math.max(3, history[i] * height);
    const x = i * (barWidth + gap);
    const y = centerY - barHeight / 2;
    const radius = barWidth / 2;
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, barWidth, barHeight, radius);
    } else {
      ctx.rect(x, y, barWidth, barHeight);
    }
    ctx.fill();
  }
}

// ── Voice Chat Page ──────────────────────────────────────────────────────────
const AskAiVoice = () => {
  const token = localStorage.getItem("token");
  const projectPid = getProjectPid();

  const [language, setLanguage] = useState("en");
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState("");

  const [messages, setMessages] = useState([
    { role: "assistant", text: FALLBACK_GREETING },
  ]);

  // ── Load dynamic welcome message ─────────────────────────────────────────
  useEffect(() => {
    if (!projectPid) return;
    getPromptByType({ projectPid, promptType: "welcome_message" })
      .then((data) => {
        const content = data?.response?.content;
        if (content) setMessages([{ role: "assistant", text: content }]);
      })
      .catch(() => {});
  }, [projectPid]);

  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceState, setVoiceState] = useState("idle"); // idle|listening|thinking|speaking|error
  // Live STT caption — full-replace on every voice:transcript_partial (never
  // append), cleared once voice:transcript_final commits the turn.
  const [liveUserText, setLiveUserText] = useState("");

  const chatRef = useRef(null);
  const socketRef = useRef(null);
  const aiSpeakingRef = useRef(false);
  // Grace-window timer before disarming local barge-in on an "idle" status —
  // see the voice:status handler for why this can't be an immediate flip.
  const aiSpeakingGraceTimeoutRef = useRef(null);
  const ignoreAudioRef = useRef(false);
  // Debounced "this turn's audio has genuinely finished playing" signal for
  // the backend — reset on every incoming chunk, so it only fires once no
  // further chunks arrive within the remaining scheduled playback window.
  const playbackDoneTimeoutRef = useRef(null);
  const micProcessorRef = useRef(null);
  const micContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const lastFinalRef = useRef("");

  // Turn / clause tracking for TTS-clause-synced captions.
  // activeTurnSeqRef holds the HIGHEST turnSeq accepted so far — turnSeq is
  // monotonically increasing per new query/interrupt, so any event with a
  // lower turnSeq is a late straggler from an already-interrupted turn and
  // must be rejected. (Resetting this to null on every new turn was the bug:
  // a late straggler from the old turn could arrive first and hijack it.)
  const activeTurnSeqRef = useRef(-1);
  // interruptedTurnSeqRef snapshots the turnSeq we just cut off. The `<` check
  // above only rejects turns strictly OLDER than the current one — it does NOT
  // reject further clauses/audio for the SAME turnSeq that were already in
  // flight over the socket at the moment of interrupt (their turnSeq is equal,
  // not less). Without this, those stragglers arrive a beat later and resume
  // appending/playing the just-cut-off response.
  const interruptedTurnSeqRef = useRef(-1);
  const clauseQueueRef = useRef([]); // clauses received, not yet matched to their first audio chunk
  const wordTimeoutsRef = useRef([]); // pending per-word reveal timeouts, exact pace from durationMs

  // Visualizer — scrolling waveform history (WhatsApp voice-note style)
  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const vizDataRef = useRef(null);
  const vizRafRef = useRef(null);
  const userSpeakingRef = useRef(false);
  const waveHistoryRef = useRef(new Array(WAVE_BAR_COUNT).fill(WAVE_MIN_LEVEL));
  const lastSampleAtRef = useRef(0);

  // ── Scroll to bottom ──────────────────────────────────────────────────────
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // ── Connect socket ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !projectPid) return;
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      auth: { token, projectPid },
    });
    socketRef.current = socket;

    socket.on("connect", () => { setSocketConnected(true); setSocketError(""); });
    socket.on("connect_error", (err) => { setSocketError(err.message); setSocketConnected(false); });
    socket.on("disconnect", () => setSocketConnected(false));

    socket.on("voice:ready", () => {
      setVoiceState("listening");
    });

    socket.on("voice:status", ({ stage }) => {
      if (stage === "thinking" || stage === "searching") {
        clearTimeout(aiSpeakingGraceTimeoutRef.current);
        aiSpeakingRef.current = false;
        ignoreAudioRef.current = false;
        setVoiceState("thinking");
      } else if (stage === "responding") {
        clearTimeout(aiSpeakingGraceTimeoutRef.current);
        aiSpeakingRef.current = true;
        ignoreAudioRef.current = false;
        setVoiceState("speaking");
      } else if (stage === "idle") {
        ignoreAudioRef.current = false;
        setVoiceState("listening");
        // Do NOT reset nextPlayTime or clauseQueueRef here — "idle" is a
        // control-plane signal meaning generation finished, not that audio
        // has finished PLAYING. Resetting nextPlayTime makes the next
        // clause's audio schedule as if it were the very first chunk ever
        // (starting near "now"), while the previous clause's buffer is still
        // actually playing — that's what caused audible overlap. Clearing
        // clauseQueueRef here can also drop a clause queued just before its
        // matching audio chunk arrives, desyncing captions from audio. Both
        // are safely reset elsewhere at points that really do end a turn:
        // voice:interrupted, voice:transcript_final, and startVoiceSession.
        // Don't disarm local barge-in immediately — "idle" also fires between
        // clauses of the same multi-sentence response, not just at the true
        // end of a turn (see note below). Flipping aiSpeakingRef false right
        // here creates a window where the user starts talking over the very
        // next clause and the near-instant local RMS barge-in is disarmed,
        // leaving only the much slower server-side STT stop-word round trip
        // to catch it — by which point that next clause has already streamed
        // and revealed most of its text. Hold armed for a short grace window;
        // cancelled in voice:tts_clause below if another clause shows up in
        // time, confirming the turn continues.
        clearTimeout(aiSpeakingGraceTimeoutRef.current);
        aiSpeakingGraceTimeoutRef.current = setTimeout(() => {
          aiSpeakingRef.current = false;
        }, 600);
        // Note: intentionally NOT closing off the streaming bubble here —
        // "idle" can also fire between per-sentence TTS clauses, and closing
        // it here would split one response across multiple message bubbles.
        // The bubble is only closed when a new turn actually starts (below).
      }
    });

    // NOTE: this used to locally detect "stop"/"रुको"/etc in the live partial
    // transcript and call endVoiceSession() — ending the ENTIRE call the
    // moment the user said "stop", instead of just interrupting the current
    // turn. The backend now handles stop-word interrupts itself (emitting the
    // normal voice:interrupted event), so this client-side shortcut was left
    // fighting the backend: it tore the mic/session down while the backend's
    // in-flight TTS for the old turn kept streaming to a torn-down session's
    // still-registered socket listeners, which is what caused "AI stops, then
    // text keeps appending a moment later" and "stuck on restart" (stale refs
    // from the aborted session never got reset). Removed — voice:interrupted
    // (see below) is the single source of truth for barge-in now.
    socket.on("voice:transcript_partial", ({ text }) => {
      // Full replace, not append — each event carries the whole accumulated
      // utterance so far, and later events can revise/correct earlier words.
      setLiveUserText(text);
    });
    // ── Word-by-word typing reveal ────────────────────────────────────────
    // Each clause carries an exact durationMs from the backend (measured
    // directly off the PCM byte length), so msPerWord = durationMs / wordCount
    // is exact — no more learning pace from a previous clause.
    const appendWord = (word) => {
      setMessages(msgs => {
        const last = msgs[msgs.length - 1];
        if (last?._voiceStreaming) {
          const sep = last.text ? " " : "";
          return [...msgs.slice(0, -1), { ...last, text: last.text + sep + word }];
        }
        return [...msgs, { role: "assistant", text: word, _voiceStreaming: true }];
      });
    };

    const revealClause = ({ words, msPerWord }) => {
      words.forEach((word, i) => {
        const id = setTimeout(() => appendWord(word), i * msPerWord);
        wordTimeoutsRef.current.push(id);
      });
    };

    const clearPendingReveals = () => {
      wordTimeoutsRef.current.forEach(id => clearTimeout(id));
      wordTimeoutsRef.current = [];
    };

    socket.on("voice:transcript_final", ({ text }) => {
      if (text === lastFinalRef.current) return;
      lastFinalRef.current = text;
      // A new turn is starting. We do NOT reset activeTurnSeqRef here — its
      // monotonic "highest turnSeq seen" guard (see declaration) is what
      // correctly rejects late stragglers from the turn we just left/interrupted.
      clauseQueueRef.current = [];
      clearPendingReveals();
      setVoiceState("thinking");
      setLiveUserText("");
      setMessages(prev => [
        ...prev.map(m => m._voiceStreaming ? { ...m, _voiceStreaming: false } : m),
        { role: "user", text },
      ]);
    });

    // Guaranteed text<->audio pairing: fired right before the audio for that
    // sentence starts streaming over voice:audio. durationMs is the exact
    // playback length of that clause's audio (computed from PCM byte length).
    socket.on("voice:tts_clause", ({ turnSeq, clauseSeq, text, durationMs }) => {
      // Reject anything strictly older than the turn we're on, AND anything
      // matching the exact turn we just interrupted — that second check is
      // what catches stragglers already in flight at the moment of interrupt.
      if (turnSeq < activeTurnSeqRef.current || turnSeq === interruptedTurnSeqRef.current) {
        return;
      }
      activeTurnSeqRef.current = turnSeq;
      // A genuinely new turn has been confirmed — safe to accept audio again.
      ignoreAudioRef.current = false;
      // A new clause just arrived — cancel any pending "disarm barge-in" grace
      // timer from an idle-between-clauses gap and re-confirm the assistant is
      // still speaking, so local barge-in detection stays armed for it.
      clearTimeout(aiSpeakingGraceTimeoutRef.current);
      aiSpeakingRef.current = true;
      const words = text.trim().split(/\s+/).filter(Boolean);
      const msPerWord = words.length && durationMs ? durationMs / words.length : FALLBACK_MS_PER_WORD;
      clauseQueueRef.current.push({ clauseSeq, words, msPerWord });
    });

    socket.on("voice:audio", ({ turnSeq, audio }) => {
      // Primary defense: reject by turnSeq, same guard as voice:tts_clause —
      // catches stale chunks for an old turn even if they were already in
      // flight over the socket when the interrupt fired. ignoreAudioRef is
      // kept as a belt-and-suspenders fallback for older backend builds that
      // don't send turnSeq on this event.
      if (turnSeq !== undefined && (turnSeq < activeTurnSeqRef.current || turnSeq === interruptedTurnSeqRef.current)) {
        return;
      }
      if (ignoreAudioRef.current) {
        return;
      }
      const buf = audio instanceof ArrayBuffer ? audio : audio.buffer;
      if (buf.byteLength < 2) return;
      // Several small audio buffers belong to one clause — only the first
      // chunk after a queued voice:tts_clause triggers the caption reveal.
      const nextClause = clauseQueueRef.current.length ? clauseQueueRef.current.shift() : null;
      if (nextClause) {
        playPCMAudio(buf, () => revealClause(nextClause));
      } else {
        playPCMAudio(buf);
      }
      // Tell the backend when THIS turn's audio has genuinely finished
      // playing — it only knows when generation/emission finished, which is
      // earlier, leaving a window where it thinks the turn is over and won't
      // barge-in-cancel it even though the client is still playing it back.
      // Debounced: reset on every chunk, only fires once nothing new shows
      // up within the remaining scheduled playback window.
      clearTimeout(playbackDoneTimeoutRef.current);
      const doneDelayMs = getScheduledAudioEndDelayMs() + 50;
      playbackDoneTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit("voice:playback_done", { turnSeq });
      }, doneDelayMs);
    });

    socket.on("voice:interrupted", (data) => {
      stopLocalPlayback();
      clearTimeout(playbackDoneTimeoutRef.current);
      clearTimeout(aiSpeakingGraceTimeoutRef.current);
      aiSpeakingRef.current = false;
      // Keep dropping audio/clauses tagged with the turn we just cut off —
      // re-enabled in voice:tts_clause only once a genuinely new turnSeq is
      // confirmed, not immediately here, so stragglers already in flight for
      // THIS turn don't sneak through and resume playback/typing a beat later.
      ignoreAudioRef.current = true;
      // Backend now sends the authoritative turnSeq of the turn actually being
      // stopped — use that instead of inferring it from activeTurnSeqRef,
      // which could theoretically be stale if a new clause updates it in the
      // same tick this event is processed. Falls back to the old inference
      // for backward compatibility if an older backend build omits it.
      interruptedTurnSeqRef.current = data?.turnSeq ?? activeTurnSeqRef.current;
      setVoiceState("listening");
      clauseQueueRef.current = [];
      clearPendingReveals();
      // Close off the truncated bubble so the next response starts fresh
      // instead of visually continuing the interrupted sentence.
      setMessages(msgs => msgs.map(m => m._voiceStreaming ? { ...m, _voiceStreaming: false } : m));
    });

    socket.on("voice:error", () => {
      setVoiceState("error");
      setTimeout(() => setVoiceState("listening"), 3000);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      wordTimeoutsRef.current.forEach(id => clearTimeout(id));
      wordTimeoutsRef.current = [];
    };
  }, [token, projectPid]);

  // ── Voice Mode ────────────────────────────────────────────────────────────
  const startVoiceSession = useCallback(async () => {
    setVoiceMode(true);
    setVoiceState("idle");
    setLiveUserText("");
    lastFinalRef.current = "";
    activeTurnSeqRef.current = -1;
    interruptedTurnSeqRef.current = -1; // stale value from a prior session must not reject this session's first clause
    ignoreAudioRef.current = false;
    clearTimeout(aiSpeakingGraceTimeoutRef.current);
    clearTimeout(playbackDoneTimeoutRef.current);
    clauseQueueRef.current = [];
    wordTimeoutsRef.current.forEach(id => clearTimeout(id));
    wordTimeoutsRef.current = [];
    socketRef.current?.emit("voice:start", { language });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      mediaStreamRef.current = stream;
      const ctx = new AudioContext();
      micContextRef.current = ctx;
      const nativeSR = ctx.sampleRate;
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      micProcessorRef.current = processor;
      source.connect(processor);
      processor.connect(ctx.destination);

      // Analyser for the live mic waveform visualizer — routed through a
      // silent gain so it's part of the pulled graph without being audible.
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      vizDataRef.current = new Uint8Array(analyser.fftSize);
      waveHistoryRef.current = new Array(WAVE_BAR_COUNT).fill(WAVE_MIN_LEVEL);
      lastSampleAtRef.current = 0;
      const silentGain = ctx.createGain();
      silentGain.gain.value = 0;
      source.connect(analyser);
      analyser.connect(silentGain);
      silentGain.connect(ctx.destination);

      const drawFrame = (ts) => {
        if (!analyserRef.current || !canvasRef.current) return;
        if (ts - lastSampleAtRef.current >= WAVE_SAMPLE_INTERVAL_MS) {
          lastSampleAtRef.current = ts;
          const amplitude = readAmplitude(analyserRef.current, vizDataRef.current);
          const history = waveHistoryRef.current;
          history.shift();
          history.push(Math.max(WAVE_MIN_LEVEL, Math.min(1, amplitude * 1.6)));
        }
        drawWaveform(canvasRef.current, waveHistoryRef.current, userSpeakingRef.current);
        vizRafRef.current = requestAnimationFrame(drawFrame);
      };
      vizRafRef.current = requestAnimationFrame(drawFrame);

      let userSpeaking = false;
      let silenceFrames = 0;
      // Require the RMS threshold to hold for several consecutive ~85-93ms
      // frames (buffer size 4096) before treating it as real speech. A cough
      // is a loud burst but still short — 3 frames (~255-280ms) rode straight
      // through one, 6 frames (~510-560ms) still let some longer/louder
      // coughs/sneezes through. 9 frames (~765-840ms) gives more margin
      // against those while still landing within a couple spoken words.
      let bargeInFrames = 0;
      const BARGE_IN_CONSECUTIVE_FRAMES = 9;

      processor.onaudioprocess = (e) => {
        const float32 = e.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let k = 0; k < float32.length; k++) sum += float32[k] ** 2;
        const rms = Math.sqrt(sum / float32.length);
        // aiSpeakingRef alone is not reliable — it can already be false
        // (grace timer expired) while audio buffers scheduled seconds ago are
        // still actually playing. Gate on the real Web Audio clock too so
        // barge-in stays armed for as long as the AI is actually audible.
        const aiActuallySpeaking = aiSpeakingRef.current || isAudioActuallyPlaying();
        userSpeakingRef.current = !aiActuallySpeaking && rms > SPEAKING_RMS_THRESHOLD;

        const ratio = nativeSR / 16000;
        const outLen = Math.floor(float32.length / ratio);
        const int16 = new Int16Array(outLen);
        for (let i = 0; i < outLen; i++) {
          int16[i] = Math.max(-32768, Math.min(32767, float32[Math.floor(i * ratio)] * 32768));
        }

        if (aiActuallySpeaking) {
          if (rms > BARGE_IN_RMS_THRESHOLD) {
            bargeInFrames++;
          } else {
            bargeInFrames = 0;
          }
          if (bargeInFrames >= BARGE_IN_CONSECUTIVE_FRAMES) {
            bargeInFrames = 0;
            clearTimeout(aiSpeakingGraceTimeoutRef.current);
            aiSpeakingRef.current = false;
            ignoreAudioRef.current = true;
            // Snapshot immediately, not just in the voice:interrupted handler —
            // closes the window between this local cut and the backend's
            // confirmation, during which already-in-flight stragglers for this
            // exact turn could otherwise still be accepted.
            interruptedTurnSeqRef.current = activeTurnSeqRef.current;
            stopLocalPlayback();
            clearTimeout(playbackDoneTimeoutRef.current);
            clauseQueueRef.current = [];
            wordTimeoutsRef.current.forEach(id => clearTimeout(id));
            wordTimeoutsRef.current = [];
            setMessages(msgs => msgs.map(m => m._voiceStreaming ? { ...m, _voiceStreaming: false } : m));
            socketRef.current?.emit("voice:interrupt");
            setVoiceState("listening");
            socketRef.current?.emit("voice:audio", int16.buffer);
          }
          return;
        }

        if (rms > 0.013) {
          silenceFrames = 0;
          userSpeaking = true;
        } else if (userSpeaking) {
          silenceFrames++;
          if (silenceFrames >= 40) {
            userSpeaking = false;
            silenceFrames = 0;
            setVoiceState("transcribing");
            socketRef.current?.emit("voice:stop");
          }
        }
        socketRef.current?.emit("voice:audio", int16.buffer);
      };
    } catch {
      setVoiceState("error");
    }
  }, [language]);

  const endVoiceSession = useCallback(() => {
    micProcessorRef.current?.disconnect();
    micContextRef.current?.close();
    mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    stopLocalPlayback();
    aiSpeakingRef.current = false;
    micProcessorRef.current = null;
    micContextRef.current = null;
    mediaStreamRef.current = null;
    if (vizRafRef.current) cancelAnimationFrame(vizRafRef.current);
    vizRafRef.current = null;
    analyserRef.current = null;
    waveHistoryRef.current = new Array(WAVE_BAR_COUNT).fill(WAVE_MIN_LEVEL);
    activeTurnSeqRef.current = -1;
    interruptedTurnSeqRef.current = -1;
    ignoreAudioRef.current = false;
    clearTimeout(aiSpeakingGraceTimeoutRef.current);
    clearTimeout(playbackDoneTimeoutRef.current);
    clauseQueueRef.current = [];
    wordTimeoutsRef.current.forEach(id => clearTimeout(id));
    wordTimeoutsRef.current = [];
    setVoiceMode(false);
    setVoiceState("idle");
    setLiveUserText("");
  }, []);

  return (
    <main className="ask-ai-page voice-chat-page">
      <div className="ai-chat-wrapper voice-chat-wrapper">

        {/* Messages */}
        <div className="ai-chat-messages" ref={chatRef}>
          {messages.map((msg, index) => (
            <div key={index} className={`message-row ${msg.role === "user" ? "user-message-row" : "ai-message-row"}`}>
              <div className="message-content">
                <div className={`message-bubble ${msg.role === "user" ? "user-bubble" : "ai-bubble"}`}>
                  {msg.text}
                </div>
              </div>
            </div>
          ))}

          {/* Live STT caption — shows the user's speech as they talk (full-replace on
              every voice:transcript_partial), with a trailing animated ellipsis so it
              reads as "still being typed" rather than a finished message. Empty-guarded
              so no blank bubble flashes before the first partial arrives. */}
          {voiceMode && liveUserText && (
            <div className="message-row user-message-row">
              <div className="message-content">
                <div className="message-bubble user-bubble voice-live-caption">
                  {liveUserText}
                  <span className="voice-caption-dots">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </span>
                </div>
              </div>
            </div>
          )}
          {voiceMode && !liveUserText && voiceState === "transcribing" && (
            <div className="message-row user-message-row">
              <div className="message-content">
                <div className="message-bubble user-bubble voice-status-bubble">
                  <span className="typing-wrap">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Voice status bubble — thinking / responding, only before text starts streaming */}
          {voiceMode && (voiceState === "thinking" || voiceState === "speaking") && !messages.some(m => m._voiceStreaming) && (
            <div className="message-row ai-message-row">
              <div className="message-content">
                <div className="message-bubble ai-bubble voice-status-bubble">
                  <span className={`voice-status-dot voice-status-dot--${voiceState}`} />
                  {voiceState === "thinking" ? "Thinking…" : "Responding…"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating assistant orb — clickable, starts/stops the session */}
        <div className="voice-orb-stage">
          <button
            className={`voice-orb-wrap${voiceMode ? " voice-orb-stop" : ""}`}
            onClick={voiceMode ? endVoiceSession : startVoiceSession}
            title={voiceMode ? "Stop voice" : "Start voice chat"}
          >
            <div className={`voice-orb-ring voice-orb-ring--outer${voiceState === "speaking" ? " voice-orb-ring--speaking" : ""}`} />
            <div className={`voice-orb-ring voice-orb-ring--inner${voiceState === "speaking" ? " voice-orb-ring--speaking" : ""}`} />
            <div className="voice-orb-core">
              <Mic size={30} color="#fff" />
            </div>
          </button>
        </div>

        {/* Socket status — only show if never connected */}
        {!socketConnected && socketError && (
          <div className="ai-socket-error">
            ⚠ Connection error: {socketError}
          </div>
        )}

        {/* Voice control bar — replaces the text input on this page */}
        <div className="ai-chat-input-area">
          <div className="voice-input-bar">
            {voiceMode ? (
              <canvas ref={canvasRef} className="voice-visualizer" width={260} height={40} />
            ) : (
              <span className="voice-input-hint">Tap the mic to start voice chat…</span>
            )}

            {!voiceMode && <LangSelect value={language} onChange={setLanguage} />}
          </div>
          <p className="ai-disclaimer">AI responses may be inaccurate. Verify important information independently.</p>
        </div>
      </div>
    </main>
  );
};

export default AskAiVoice;
